import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { FieldStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import React, { useState } from 'react'

type ActionChoice = 'keep' | 'edit' | 'clear'

interface IEditableFieldProps {
  label: string
  store: FieldStore<any | null | undefined>
  options?: { value: any; label: string }[]
  children?: (value: any, onChange: (newValue: any) => void) => React.ReactNode
  disableClear?: boolean
  isRequired?: boolean
  disabled?: boolean
  disabledText?: string

  choice?: ActionChoice
  onChoiceChange?: (newChoice: ActionChoice) => void

  dataCy?: string
}

export const EditableField: React.FC<IEditableFieldProps> = observer(
  ({
    label,
    store,
    options,
    children,
    disableClear = false,
    isRequired = false,
    disabled = false,
    disabledText,
    choice,
    onChoiceChange,
    dataCy,
  }) => {
    const { t } = useTranslate()

    const [internalChoice, setInternalChoice] = useState<ActionChoice>('keep')

    const isControlled = choice !== undefined && onChoiceChange !== undefined
    const currentChoice = isControlled ? choice : internalChoice

    const handleActionChange = (event: SelectChangeEvent<string>) => {
      const newChoice = event.target.value as ActionChoice

      if (isControlled) {
        onChoiceChange(newChoice)
      } else {
        setInternalChoice(newChoice)
      }

      if (newChoice === 'keep') {
        store.setValue(undefined)
      } else if (newChoice === 'clear' && !disableClear) {
        store.setValue(null)
      }
    }

    const handleValueChange = (newValue: any) => {
      store.setValue(newValue)
    }

    const handleSelectChange = (event: SelectChangeEvent<any>) => {
      store.setValue(event.target.value)
    }

    return (
      <Box sx={{ width: '100%' }} data-cy={dataCy && dataCy}>
        <Typography variant='subtitle2'>{label}</Typography>
        <FormControl fullWidth disabled={disabled}>
          <Select
            value={currentChoice}
            onChange={handleActionChange}
            data-cy={dataCy && `${dataCy}-action-select`}
          >
            <MenuItem value='keep' data-cy={dataCy && `${dataCy}-action-keep`}>
              {disabled && disabledText
                ? disabledText
                : t('keepCurrentValue', 'Keep current value')}
            </MenuItem>
            <MenuItem value='edit' data-cy={dataCy && `${dataCy}-action-edit`}>
              {t('editCurrentValue', 'Edit current value')}
            </MenuItem>
            {!disableClear && (
              <MenuItem value='clear' data-cy={dataCy && `${dataCy}-action-clear`}>
                {t('clear', 'Clear')}
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {currentChoice === 'edit' && (
          <Box sx={{ mt: 1 }}>
            {children ? (
              children(store.value, handleValueChange)
            ) : (
              <FormControl fullWidth required={isRequired} error={!!store.error}>
                <InputLabel>{label}</InputLabel>
                <Select
                  label={label}
                  value={store.value ?? ''}
                  onChange={handleSelectChange}
                  required={isRequired}
                  error={!!store.error}
                  data-cy={dataCy && `${dataCy}-value-select`}
                >
                  {options?.map(option => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      data-cy={dataCy && `${dataCy}-value-option-${option.value}`}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {store.error && <FormHelperText>{store.error}</FormHelperText>}
              </FormControl>
            )}
          </Box>
        )}
      </Box>
    )
  },
)
